import React, { forwardRef, useCallback, useRef } from 'react';
import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import { isEmpty } from 'lodash';

import type { PopoverAlign, PopoverOffset, PopoverSide } from '~/components/ui/atoms/Popover';
import { writePopupHeight } from '~/components/ui/internal/popupGrow';
import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

export interface PreviewCardProps {
  /**
   * The element that shows the card while it is hovered or has keyboard focus. It is rendered
   * inside a `<span>` that takes the hover and focus handlers, so it does not need to forward a ref.
   */
  trigger: React.ReactNode;
  /** The card's content: a preview of what the trigger links to. */
  children?: React.ReactNode;
  /**
   * The side of the trigger the card sits on. It flips to the opposite side when it does not fit.
   * @default 'bottom'
   */
  side?: PopoverSide;
  /** @default 'center' */
  align?: PopoverAlign;
  /**
   * Distance from the trigger along the side, in pixels. The default leaves room for the arrow.
   * @default 12
   */
  sideOffset?: PopoverOffset;
  /** Nudge along the alignment axis, in pixels. */
  alignOffset?: PopoverOffset;
  /**
   * How long the pointer rests on the trigger, or the trigger keeps keyboard focus, before the card
   * opens, in milliseconds.
   * @default 300
   */
  delay?: number;
  /**
   * How long the card stays after the pointer leaves the trigger and the card, in milliseconds.
   * @default 200
   */
  closeDelay?: number;
  /** Applied to the card surface. */
  className?: string;
}

// The space kept between the card and the viewport edges, as on the Popover.
const VIEWPORT_MARGIN = 16;

// See the Portal below: the card stacks on the modal layer, above a dialog it opens from.
const portalClasses = 'relative z-modal';

// The Popover's surface and grow transition (the `popover-popup` rules in index.css), without its
// scrolling, which would clip the arrow.
const popupClasses = 'popover-popup rounded-md bg-white shadow-elevation-8 outline-none max-w-[calc(100vw-32px)]';

// A white square turned 45 degrees, half of it outside the card's edge on the trigger's side. Base UI
// places it along that edge, over the trigger's center. The shadow falls on the outer half only.
// The spacing scale has no 6, so the offsets are arbitrary values.
const arrowClasses = cn(
  'h-12 w-12 rotate-45 bg-white',
  'data-[side=bottom]:-top-[6px] data-[side=bottom]:shadow-[-2px_-2px_4px_rgba(0,0,0,0.1)]',
  'data-[side=top]:-bottom-[6px] data-[side=top]:shadow-[2px_2px_4px_rgba(0,0,0,0.1)]',
  'data-[side=left]:-right-[6px] data-[side=left]:shadow-[2px_-2px_4px_rgba(0,0,0,0.1)]',
  'data-[side=right]:-left-[6px] data-[side=right]:shadow-[-2px_2px_4px_rgba(0,0,0,0.1)]'
);

const PreviewCard = forwardRef<HTMLDivElement, PreviewCardProps>(
  (
    {
      trigger,
      children,
      side = 'bottom',
      align = 'center',
      sideOffset = 12,
      alignOffset = 0,
      delay = 300,
      closeDelay = 200,
      className,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to PreviewCard: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the PreviewCardProps interface and to the component's signature.`,
        true
      );
    }

    const triggerRef = useRef<HTMLSpanElement | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const setPopupRef = useCallback(
      (element: HTMLDivElement | null) => {
        popupRef.current = element;
        if (element) {
          writePopupHeight(element);
        }
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref]
    );

    const handleOpenChange = (open: boolean) => {
      if (!open && popupRef.current) {
        writePopupHeight(popupRef.current);
      }
    };

    return (
      <BasePreviewCard.Root onOpenChange={handleOpenChange}>
        {/* `inline-flex` keeps the span to the trigger's size: as an inline box it would add a text
            line's height below it. */}
        <BasePreviewCard.Trigger
          delay={delay}
          closeDelay={closeDelay}
          render={<span ref={triggerRef} className="inline-flex" />}
        >
          {trigger}
        </BasePreviewCard.Trigger>
        {/* Straight into the body, mounted when it opens, as a z-modal stacking context: a card
            shown from inside a MUI dialog stacks on top of it. */}
        <BasePreviewCard.Portal container={document.body} className={portalClasses}>
          <BasePreviewCard.Positioner
            // The trigger element itself rather than the span around it, which also wraps the
            // trigger's margins, so the arrow and the gap measure from what the user sees.
            anchor={() => triggerRef.current?.firstElementChild ?? triggerRef.current}
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            collisionPadding={VIEWPORT_MARGIN}
          >
            <BasePreviewCard.Popup
              data-fs-component="preview-card"
              ref={setPopupRef}
              className={cn(popupClasses, className)}
            >
              <BasePreviewCard.Arrow className={arrowClasses} />
              {children}
            </BasePreviewCard.Popup>
          </BasePreviewCard.Positioner>
        </BasePreviewCard.Portal>
      </BasePreviewCard.Root>
    );
  }
);

PreviewCard.displayName = 'PreviewCard';

export default PreviewCard;
